import "client-only"

import { AppChannel, Channel } from "@/config/channel"
import { useConfigParams } from "@/hooks/use-config-params"
import { TAuthType } from "@/lib/auth-type"
import {
  buildAuthenticatedReservedAreaLink,
  buildCommunityRedirectUrl,
  getAppLoginCallbackUrl,
  isPortalUrl,
  isReservedAreaUrl,
} from "@/lib/callback-url"
import {
  CommunityProfileStatus,
  useCommunityProfileStatus,
} from "@/services/get-community-info-user-me"
import {
  useLoginData,
  useLoginDataWithMfaConfirm,
} from "@/services/hooks/use-login-data"
import { TConfirmLoginResponse } from "@/services/post-login-confirm"
import { TOAuthLoginV2Response } from "@/services/post-login-oauth-v2"
import { TSsoLoginResponse } from "@/services/post-login-sso"
import Cookies from "js-cookie"

export function usePostLoginRedirectUrl() {
  const { endCallbackUrl, flagConto, authType, channel, communityShare } =
    useConfigParams()

  const previousConfirmResponseData = useLoginDataWithMfaConfirm()
  const previousLoginResponseData = useLoginData() as TOAuthLoginV2Response
  const prevResponseDataComposition =
    previousConfirmResponseData || previousLoginResponseData?.redirect
      ? {
          ...previousConfirmResponseData,
          redirect: previousLoginResponseData?.redirect,
        }
      : undefined
  const previousCommunityProfileStatusSWR = useCommunityProfileStatus()

  return (
    loginResponseData?: TConfirmLoginResponse & { redirect?: string },
    communityProfileStatus?: CommunityProfileStatus,
  ) => {
    // BEGIN - determine which data to use
    // if provided, use the data from the caller's argument because they are from a flow that requires fresh data,
    // otherwise fallback to the previously stored data
    const loginData =
      loginResponseData ??
      (prevResponseDataComposition as TConfirmLoginResponse & {
        redirect?: string
      })
    const communityProfStatus =
      communityProfileStatus ??
      previousCommunityProfileStatusSWR.data ??
      "UNKNOWN_COMMUNITY_PROFILE_STATUS"
    // END - determine which data to use

    if (!loginData || !endCallbackUrl) return ""

    return getRedirectUrl(
      loginData,
      endCallbackUrl,
      flagConto,
      authType,
      channel,
      communityProfStatus,
      communityShare,
    )
  }
}

function getRedirectUrl<T extends TSsoLoginResponse & { redirect?: string }>(
  response: T,
  endcallbackurl: string,
  flagConto: number,
  authType: TAuthType,
  channel: Channel,
  communityProfileStatus: CommunityProfileStatus,
  communityShare?: string,
) {
  if (!response || response.isLoginBlocked) return ""

  if (
    authType === "oauth" &&
    "redirect" in response &&
    typeof response.redirect === "string"
  ) {
    return response.redirect
  }

  const token = response.jwt

  switch (channel) {
    case AppChannel.PortaleWebFullResponsiveWwwSisalIt:
      if (token && isReservedAreaUrl(endcallbackurl)) {
        return buildAuthenticatedReservedAreaLink(endcallbackurl, token)
      } else if (
        isPortalUrl(endcallbackurl) &&
        (communityProfileStatus != "UNKNOWN_COMMUNITY_PROFILE_STATUS" ||
          communityShare)
      ) {
        return buildCommunityRedirectUrl(
          communityProfileStatus,
          communityShare,
        ).toString()
      } else if (endcallbackurl && isSameDomain(endcallbackurl)) {
        // REM0001421
        return endcallbackurl
      } else {
        return window.APP_MAIN_SITE_HOMEPAGE
      }
    case AppChannel.SalvaIlBottino: {
      return computeSalvaIlBottinoRedirectUrl(endcallbackurl, token!)
    }
    default:
      return getAppLoginCallbackUrl(token!, response.tokenTouch!, flagConto)
  }
}

function computeSalvaIlBottinoRedirectUrl(
  endcallbackurl: string,
  token: string,
) {
  let url = `${endcallbackurl}/?auth-token=${token}`

  const AK_AALB_COOKIE_NAME = window.LOGIN_AK_AALB_COOKIE_NAME

  const akaalbAreaPrivataK8sCookie = Cookies.get(AK_AALB_COOKIE_NAME)
  if (akaalbAreaPrivataK8sCookie != null) {
    const s = akaalbAreaPrivataK8sCookie.split("farm-")[1]
    url += `&f=${s != null ? s.charAt(0) : ""}`
  }
  return url
}

function isSameDomain(endcallbackurl: string) {
  // this is only valid assuming two parts first-level domains, that is the case for current Flutter domains
  const currentDomain = window.location.hostname.split(".").slice(-2).join(".")

  const targetUrl = new URL(endcallbackurl)
  const targetDomain = targetUrl.hostname.split(".").slice(-2).join(".")

  return currentDomain === targetDomain
}
